// src/pages/EditTimer.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import api from '../../api/axiosInstance';
import { FiSave, FiType, FiClock, FiSliders, FiEye, FiAlertCircle } from 'react-icons/fi';

// --- Child Component: Live Timer Preview (Reused from CreateTimer) ---
const TimerPreview = ({ backgroundColor, digitColor, labelColor, endTime }) => {
    const calculateTimeLeft = useCallback(() => {
        const difference = +new Date(endTime) - +new Date();
        let timeLeft = {};

        if (difference > 0) {
            timeLeft = {
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60),
            };
        }
        return timeLeft;
    }, [endTime]);

    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

    useEffect(() => {
        const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
        return () => clearInterval(timer);
    }, [endTime, calculateTimeLeft]);

    const formatTime = (value) => value.toString().padStart(2, '0');

    const timeSegments = [
        { label: 'Days', value: timeLeft.days ?? 0 },
        { label: 'Hours', value: timeLeft.hours ?? 0 },
        { label: 'Minutes', value: timeLeft.minutes ?? 0 },
        { label: 'Seconds', value: timeLeft.seconds ?? 0 },
    ];

    return (
        <div
            className="p-8 rounded-lg flex items-center justify-center transition-colors duration-300"
            style={{ backgroundColor: backgroundColor }}
        >
            <div className="flex items-center justify-center gap-4 sm:gap-6">
                {timeSegments.map((segment) => (
                    <div key={segment.label} className="text-center">
                        <div
                            className="text-4xl sm:text-6xl font-bold transition-colors duration-300"
                            style={{ color: digitColor }}
                        >
                            {formatTime(segment.value)}
                        </div>
                        <div
                            className="text-xs sm:text-sm uppercase tracking-wider mt-1 transition-colors duration-300"
                            style={{ color: labelColor }}
                        >
                            {segment.label}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- Main Edit Timer Component ---
const EditTimer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [setBreadcrumb, setBreadcrumbLink] = useOutletContext();

    // Form State
    const [name, setName] = useState('');
    const [time, setTime] = useState('');
    const [backgroundColor, setBackgroundColor] = useState('#0066FF');
    const [digitColor, setDigitColor] = useState('#ffffff');
    const [labelColor, setLabelColor] = useState('#ffffff');

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setBreadcrumb(['Countdown', 'Edit Timer']);
        setBreadcrumbLink(['/countdown']);
    }, [setBreadcrumb, setBreadcrumbLink]);

    useEffect(() => {
        const fetchTimerData = async () => {
            setIsLoading(true);
            try {
                const response = await api.get(`/countdown/metadata/${id}`);
                const timer = response.data.timer;
                setName(timer.name);
                setBackgroundColor(timer.backgroundColor);
                setDigitColor(timer.digitColor);
                setLabelColor(timer.labelColor);

                // Convert Unix timestamp (seconds) to a format for datetime-local input
                const date = new Date(timer.endTime * 1000);
                const timezoneOffset = date.getTimezoneOffset() * 60000;
                const localDate = new Date(date.getTime() - timezoneOffset);
                const formattedDate = localDate.toISOString().slice(0, 16);
                setTime(formattedDate);

            } catch (err) {
                setError(err.message === 'Network Error' ? "No Internet Connection." : 'Failed to load timer data.');
                console.error(`Error fetching timer:`, err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTimerData();
    }, [id]);

    const handleUpdateTimer = async (event) => {
        event.preventDefault();
        setError('');
        if (!name || !time) {
            setError('Please fill out all required fields.');
            return;
        }

        setIsSaving(true);
        try {
            const res = await api.put(`/countdown/${id}`, {
                name,
                endTime: Math.floor(new Date(time).getTime() / 1000),
                backgroundColor,
                digitColor,
                labelColor,
            });
            if (!res.data.error) {
                navigate('/countdown');
            } else {
                setError(res.data.message || 'An unknown error occurred.');
            }
        } catch (err) {
            setError(err.message === 'Network Error' ? "No Internet Connection." : 'Failed to save timer.');
            console.error('Error updating timer:', err);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <>
                <div className="flex items-center justify-center w-full h-[90vh]">
                    <div role="status">
                        <svg aria-hidden="true" className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" /><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" /></svg>
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            </>
        );
    }

    return (
        <div className="p-4 sm:p-6 bg-brand-background min-h-screen font-sans">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-brand-heading">Edit Timer</h1>
                    <p className="text-brand-paragraph mt-1">Update the configuration and see changes in real-time.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* --- Left Column: Form --- */}
                <div className="lg:col-span-3 bg-white p-6 rounded-lg border border-brand-border shadow-sm">
                    <form onSubmit={handleUpdateTimer}>
                        <div className="space-y-6">
                            {/* --- Section 1: Timer Details --- */}
                            <div>
                                <h3 className="text-lg font-bold text-brand-heading flex items-center gap-2 mb-3"><FiType /> Timer Details</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-900">Timer Name</label>
                                        <input onChange={(e) => setName(e.target.value)} value={name} type="text" id="name" className="bg-gray-50 border border-brand-border text-gray-900 text-sm rounded-lg focus:ring-brand-accent focus:border-brand-accent block w-full p-2.5" placeholder="e.g., Product Launch" required />
                                    </div>
                                    <div>
                                        <label htmlFor="endTime" className="block mb-2 text-sm font-medium text-gray-900 flex items-center gap-2"><FiClock /> End Date & Time</label>
                                        <input onChange={(e) => setTime(e.target.value)} value={time} type="datetime-local" id="endTime" className="bg-gray-50 border border-brand-border text-gray-900 text-sm rounded-lg focus:ring-brand-accent focus:border-brand-accent block w-full p-2.5" required />
                                    </div>
                                </div>
                            </div>

                            {/* --- Section 2: Styling --- */}
                            <div>
                                <h3 className="text-lg font-bold text-brand-heading flex items-center gap-2 mb-3"><FiSliders /> Styling</h3>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="text-center">
                                        <label htmlFor="backgroundColor" className="block mb-2 text-sm font-medium text-gray-900">Background</label>
                                        <input onChange={(e) => setBackgroundColor(e.target.value)} value={backgroundColor} type="color" id="backgroundColor" className="p-1 h-12 w-full block bg-white border border-gray-200 cursor-pointer rounded-lg" required />
                                    </div>
                                    <div className="text-center">
                                        <label htmlFor="digitColor" className="block mb-2 text-sm font-medium text-gray-900">Digits</label>
                                        <input onChange={(e) => setDigitColor(e.target.value)} value={digitColor} type="color" id="digitColor" className="p-1 h-12 w-full block bg-white border border-gray-200 cursor-pointer rounded-lg" required />
                                    </div>
                                    <div className="text-center">
                                        <label htmlFor="labelColor" className="block mb-2 text-sm font-medium text-gray-900">Labels</label>
                                        <input onChange={(e) => setLabelColor(e.target.value)} value={labelColor} type="color" id="labelColor" className="p-1 h-12 w-full block bg-white border border-gray-200 cursor-pointer rounded-lg" required />
                                    </div>
                                </div>
                            </div>

                            {error && (
                                <div className="bg-red-50 text-red-700 p-3 rounded-lg flex items-center gap-2 text-sm">
                                    <FiAlertCircle /> {error}
                                </div>
                            )}

                            <div className="pt-4 border-t border-gray-100">
                                <button type="submit" className="w-full flex items-center justify-center gap-2 text-white bg-brand-accent hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-3 text-center disabled:opacity-70" disabled={isSaving}>
                                    {isSaving ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <><FiSave /> Save Changes</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                {/* --- Right Column: Preview --- */}
                <div className="lg:col-span-2">
                    <div className="sticky top-24">
                        <h3 className="text-lg font-bold text-brand-heading flex items-center gap-2 mb-3"><FiEye /> Live Preview</h3>
                        <div className="bg-white p-1 rounded-lg border border-brand-border shadow-sm">
                            <TimerPreview
                                backgroundColor={backgroundColor}
                                digitColor={digitColor}
                                labelColor={labelColor}
                                endTime={time}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditTimer;
